import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCellMui from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from '@mui/material/FormControlLabel';
import { visuallyHidden } from "@mui/utils";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Paper from "@mui/material/Paper";
import CircularProgress from '@mui/material/CircularProgress';

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CachedIcon from "@mui/icons-material/Cached";
import AppRegistrationOutlinedIcon from '@mui/icons-material/AppRegistrationOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import EditOutlined from "@mui/icons-material/EditOutlined";
import MinimalPage from "../../layout/MinimalPage";
import api from "../../service/api";
import { setError } from "../../reducers/appSlice";
import Recepcionar from "../../commons/Recepcionar";
import Estados from "../../commons/Estados";
import Archivar from "../../commons/Archivar";
import VisorPdf from "../../components/Visorpdf";
import Modal from "../../components/Modal";
import Accion from "../../components/Accion";

const TableCellHead = styled(TableCellMui)(({ theme }) => ({
  color: "#3c4b64",
  padding: theme.spacing(1, 1, 1, 1),
}));

const TableCell = styled(TableCellMui)(({ theme }) => ({
  padding: theme.spacing(1, 1, 1, 1),
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButtonGroup-grouped": {
    margin: theme.spacing(0.5),
    border: 0,
    "&.Mui-disabled": {
      border: 0,
    },
    "&:not(:first-of-type)": {
      borderRadius: theme.shape.borderRadius,
    },
    "&:first-of-type": {
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "expediente",
    numeric: true,
    disablePadding: true,
    label: "Expediente",
    width: 150,
    align: "center",
  },
  {
    id: "tramiteInterno",
    numeric: true,
    disablePadding: false,
    label: "Trámite interno",
    width: 150,
    align: "center",
  },
  {
    id: "descOrgOrigen",
    numeric: false,
    disablePadding: false,
    label: "Organismo de origen",
    width: 400,
    align: "left",
  },
  {
    id: "asunto",
    numeric: false,
    disablePadding: false,
    label: "Asunto",
    width: 400,
    align: "left",
  },
  {
    id: "folios",
    numeric: false,
    disablePadding: false,
    label: "Folios",
    width: 50,
    align: "center",
  },
  {
    id: "soporte",
    numeric: false,
    disablePadding: false,
    label: "Soporte",
    width: 75,
    align: "center",
  },
  {
    id: "recibido",
    numeric: false,
    disablePadding: false,
    label: "Recibido",
    width: 50,
  },
];

function EnhancedTableHead(props) {
  const {
    order,
    orderBy,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" width={50}>
          {" "}
        </TableCell>
        {headCells.map((headCell) => (
          <TableCellHead
            key={headCell.id}
            align={headCell.align}
            padding="normal"
            sortDirection={orderBy === headCell.id ? order : false}
            width={headCell.width}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              <b>{headCell.label}</b>
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc"
                    ? "ordenado descendente"
                    : "ordenado ascendente"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCellHead>
        ))}
        <TableCellHead width={200} align="center">
          <b>Acciones</b>
        </TableCellHead>
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  //onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const TablePaginationActions = (props) => {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
            <KeyboardArrowLeft />
          )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
            <KeyboardArrowRight />
          )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const validationSchema = Yup.object().shape({
  nroExpediente: Yup.number()
    .integer("Debe ingresar un número entero")
    .positive("Debe ingresar un número positivo"),

  nroTramite: Yup.number()
    .integer("Debe ingresar un número entero")
    .positive("Debe ingresar un número positivo"),

  digital: Yup.string(),
  fecha: Yup.date(),
});

const EnhancedTableToolbar = (props) => {
  const { numSelected, selected, resetearTabla, submitting } = props;
  const [alignment, setAlignment] = React.useState("left");
  const [recargar, setRecargar] = React.useState("left");

  const handleAlignment = (event, newAlignment) => {
    setAlignment(newAlignment);
  };

  const handleRecargar = (event, newRecargar) => {
    setRecargar(newRecargar);
  };

  return (
    <>
      <Grid container sx={{ display: { xs: "inline", sm: "none" } }}>
        <Grid item xs={12} lg={12} align="right">
          <Accion funcion={props.funcion} accion="alta">
            <IconButton
              title="Nuevo"
              size="medium"
              component={Link}
              to={"/alta_tramite/"}
              sx={{
                color: "#fff",
                backgroundColor: "#799F4F",
                "&:hover": { color: "#fff", backgroundColor: "#546f37" },
                margin: 1,
              }}
            >
              <AddIcon />
            </IconButton>
          </Accion>
        </Grid>
      </Grid>
      <Toolbar disableGutters>
        <Grid container spacing={1} align="left">
          <Grid item align="left">
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: "row",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                flexWrap: "wrap",
              }}
            >
              <Accion funcion={props.funcion} accion="imprimir_caratula">
                <StyledToggleButtonGroup
                  size="small"
                  value={alignment}
                  exclusive
                  onChange={handleAlignment}
                  aria-label="text accionGrupal"
                >
                  <Recepcionar
                    activo={"S"}
                    mostrar="icono"
                    numSelected={numSelected}
                    datos={selected}
                    resultado={resetearTabla}
                    submitting={submitting}
                  />
                  <Estados
                    activo={"S"}
                    mostrar="icono"
                    numSelected={numSelected}
                    datos={selected}
                    resultado={resetearTabla}
                    submitting={submitting}
                  />
                  <Archivar
                    activo={"S"}
                    mostrar="icono"
                    numSelected={numSelected}
                    datos={selected}
                    resultado={resetearTabla}
                    submitting={submitting}
                  />
                </StyledToggleButtonGroup>
              </Accion>
            </Paper>
          </Grid>
          <Grid item align="left" sx={{ display: "flex", alignItems: 'center' }}>
            <IconButton
              title="Recargar la bandeja de entrada"
              size="medium"
              disabled={submitting}
              onClick={resetearTabla}
            >
              <RefreshOutlinedIcon />
            </IconButton>
          </Grid>
        </Grid>

        <Grid container sx={{ display: { xs: "none", sm: "block" } }}>
          <Grid item xs={12} sm={12} md={12} lg={12} align="right">
            <Accion funcion={props.funcion} accion="alta">
              <Button
                variant="verde"
                sx={{ m: 1 }}
                endIcon={<AddIcon />}
                component={Link}
                to={"/alta_tramite/"}
              >
                Nuevo
            </Button>
            </Accion>
          </Grid>
        </Grid>
      </Toolbar>
    </>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};


const BandejaEntrada = (props) => {
  const dispatch = useDispatch();

  const location = useLocation();
  const breadcrumbs = [
    { nombre: "Inicio", ruta: "/" },
    { nombre: "Bandeja de entrada", ruta: "" },
  ];
  const [datos, setDatos] = useState([]);
  const [filtros, setFiltros] = useState({
    nroExpediente: "",
    nroTramite: "",
    digital: "T",
    fecha: "",
    paraFirmar: false,
    asignadosaMi: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("Expediente");
  const [selected, setSelected] = useState([]);

  const [page, setPage] = React.useState(0);

  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [lblCriterios, setLblCriterios] = useState("Criterios de búsqueda");

  const [openDocumento, setOpenDocumento] = useState(false);

  const [documento, setDocumento] = useState("");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = datos.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name, isItemSelected) => {

    // console.log(selected.indexOf(name));  
    // console.log(isItemSelected);  

    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    // verificamos que el trámite no se repita en la selección siempre y cuando se chequea y no cuando se deschequea 
    if ((selected.find((selected) => selected.id === name.id)) && (isItemSelected === false)) {
      // no debe agregar
      dispatch(setError("El trámite ya se encuentra seleccionado."));
    } else {
      setSelected(newSelected);
    }

  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseDocument = () => {
    setOpenDocumento(false);
  };

  const handleChangeTextField = (event) => {
    if (/^\d*$/.test(event.target.value)) {
      formik.setFieldValue(event.target.id, event.target.value);
    }
  };

  const handleChangeCheck = (event) => {
    if (event.target.checked) {
      formik.setFieldValue(event.target.id, true);
      return;
    } else {
      formik.setFieldValue(event.target.id, false);
      return;
    }
  }

  const verCaratula = async (tramiteId) => {
    try {
      let base64 = await api.obtenerCaratula({ tramiteId: tramiteId });
      setDocumento(base64.data);
      setOpenDocumento(true);
    } catch (e) {
      dispatch(setError("Error al obtener la carátula"));
    }
  };

  const verVolante = async (tramiteId) => {
    try {
      let base64 = await api.obtenerVolante({ tramiteId: tramiteId });
      setDocumento(base64.data);
      setOpenDocumento(true);
    } catch (e) {
      dispatch(setError("Error al obtener el volante"));
    }
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - datos.length) : 0;

  const formik = useFormik({
    initialValues: {
      nroExpediente: "",
      nroTramite: "",
      digital: "T",
      fecha: "",
      paraFirmar: false,
      asignadosaMi: false,
    },
    lblCriterios: "Criterios de búsqueda",
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setDatos([]);
      setLblCriterios("Criterios de búsqueda");
      setPage(0);
      formik.values.nroExpediente !== "" ||
        formik.values.nroTramite !== "" ||
        formik.values.digital !== "T" ||
        formik.values.paraFirmar ||
        formik.values.asignadosaMi
        ? setLblCriterios("Criterios aplicados")
        : setLblCriterios("Criterios de búsqueda");
      buscarTramites(values);
    },
    onReset: (values) => {
      setSelected([]);
      setDatos([]);
      setLblCriterios("Criterios de búsqueda");
      buscarTramites(values);
      formik.values.paraFirmar = false;
      formik.values.asignadosaMi = false;
    },
  });

  const buscarTramites = (filtros) => {
    setSubmitting(true);
    setDatos([]);
    api
      .bandejaEntrada(filtros)
      .then((res) => {
        setDatos(res.data);
        setFiltros(res.data);
        //setSelected([]);
      })
      .catch((e) => {
        dispatch(setError(e.response.data.error));
        console.log("Error al cargar los datos");
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    buscarTramites(filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log(formik.errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.errors]);


  return (
    <MinimalPage breadcrumbs={breadcrumbs} disableCheckAuth>
      <Accordion sx={{ pl: 1, pr: 1 }} disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-filtros"
          id="panel-filtros"
          sx={{ height: "50px" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <FilterAltOutlinedIcon
              color={submitting ? "secondary" : "primary"}
              sx={{ mr: 1, display: { xs: "none", md: "inline" } }}
            />
            <Typography variant="cuerpo">{lblCriterios}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1 }}>
          <form
            id="buscador"
            name="buscador"
            onSubmit={formik.handleSubmit}
            onReset={formik.handleReset}
            noValidate
            autoComplete="off"
          >
            <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
              <Grid item xs={12} lg={3} sx={{ mb: 1 }}>
                <TextField
                  id="nroExpediente"
                  name="nroExpediente"
                  label="Número de expediente"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formik.values.nroExpediente}
                  onChange={handleChangeTextField}
                  error={
                    formik.touched.nroExpediente &&
                    Boolean(formik.errors.nroExpediente)
                  }

                  helperText={
                    formik.touched.nroExpediente && formik.errors.nroExpediente
                  }
                />
              </Grid>
              <Grid item xs={12} lg={3} sx={{ mb: 1 }}>
                <TextField
                  id="nroTramite"
                  name="nroTramite"
                  label="Número de trámite"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formik.values.nroTramite}
                  onChange={handleChangeTextField}
                  error={
                    formik.touched.nroTramite &&
                    Boolean(formik.errors.nroTramite)
                  }
                  helperText={
                    formik.touched.nroTramite && formik.errors.nroTramite
                  }
                />
              </Grid>
              <Grid item xs={12} lg={3} sx={{ mb: 1 }}>
                <FormControl
                  variant="outlined"
                  size="small"
                  error={
                    formik.touched.digital && Boolean(formik.errors.digital)
                  }
                  fullWidth
                >
                  <InputLabel id="digitalLabel">Soporte</InputLabel>
                  <Select
                    labelId="digitalLabel"
                    id="digital"
                    name="digital"
                    variant="outlined"
                    label="Soporte"
                    value={formik.values.digital}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="T">TODOS</MenuItem>
                    <MenuItem value="S">DIGITAL</MenuItem>
                    <MenuItem value="N">PAPEL</MenuItem>
                  </Select>
                  <FormHelperText>
                    {formik.touched.digital && formik.errors.digital}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} lg={3} sx={{ mb: 1 }}>
                <FormControl
                  variant="outlined"
                  size="small"
                  error={
                    formik.touched.fecha && Boolean(formik.errors.fecha)
                  }
                  fullWidth
                >
                  <TextField
                    id="fecha"
                    type="date"
                    value={formik.values.fecha}
                    label="Ingresados el"
                    variant="outlined"
                    size="small"
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }} />
                  <FormHelperText>
                    {formik.touched.fecha && formik.errors.fecha}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={1} xs={12} lg={6} sx={{ pl: 2, pr: 2 }}>
              <Grid item xs={12} lg={6} sx={{ mb: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="paraFirmar"
                      color="primary"
                      value={formik.values.paraFirmar}
                      checked={formik.values.paraFirmar}
                      inputProps={{
                        "aria-labelledby": "Para firmar",
                      }}
                      onChange={handleChangeCheck}
                    />
                  }
                  label="Para firmar"
                  sx={{ fontSize: "12px", color: "#3c4b64" }} />
              </Grid>
              <Grid item xs={12} lg={6} sx={{ mb: 1, pt: 0 }} >
                <FormControlLabel
                  control={
                    <Checkbox
                      id="asignadosaMi"
                      color="primary"
                      value={formik.values.asignadosaMi}
                      checked={formik.values.asignadosaMi}
                      inputProps={{
                        "aria-labelledby": "Recibidos por mí",
                      }}
                      onChange={handleChangeCheck}
                    />
                  }
                  label="Recibidos por mí"
                  sx={{ fontSize: "12px", color: "#3c4b64", pt: 0 }} />
              </Grid>
            </Grid>
            <Grid item lg={12} xs={12} align="right" sx={{ pr: 1 }}>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Button
                  variant="plata"
                  type="reset"
                  sx={{ m: 1 }}
                  endIcon={<CachedIcon />}
                >
                  Limpiar
                </Button>{" "}
                <Button
                  variant="gris"
                  type="submit"
                  disabled={submitting}
                  sx={{ m: 1 }}
                  endIcon={<SearchIcon />}
                >
                  {submitting ? "Buscando..." : "Buscar"}
                </Button>
              </Box>
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <IconButton color="primary" type="reset" title="Limpiar">
                  <CachedIcon />
                </IconButton>{" "}
                <IconButton
                  color="primary"
                  type="submit"
                  disabled={submitting}
                  title="Buscar"
                >
                  <SearchIcon />
                </IconButton>
              </Box>
            </Grid>
          </form>
        </AccordionDetails>
      </Accordion>
      <br></br>
      <Card>
        <CardContent>
          <Typography variant="aclaracion" gutterBottom>
            {selected.length === 1
              ? " (" + selected.length + " seleccionado)"
              : " (" + selected.length + " seleccionados)"}
          </Typography>
          <EnhancedTableToolbar
            numSelected={selected.length}
            selected={selected}
            resetearTabla={() => buscarTramites(formik.values, setSelected([]))}
            funcion={props.funcion}
            submitting={submitting}
          />
          <TableContainer>
            <Table
              sx={{ minWidth: 750, border: "1px solid #dee2e6" }}
              aria-labelledby="tableTitle"
              size="small"
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={datos.length}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
                {stableSort(datos, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((dato, index) => {

                    if (selected.find((selected) => selected.id === dato.id)) {

                    }
                    const isItemSelected = isSelected(dato);
                    const labelId = `enhanced-table-checkbox-${index}`;


                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={dato.id}
                        selected={isItemSelected}
                        sx={
                          index % 2 === 0
                            ? { backgroundColor: "#f2f2f2" }
                            : { backgroundColor: "#fff" }
                        }
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={selected.find((selected) => selected.id === dato.id)}
                            inputProps={{
                              "aria-labelledby": labelId,
                            }}
                            onClick={(event) => handleClick(event, dato, isItemSelected)}
                          />
                        </TableCell>
                        <TableCell
                          id={labelId}
                          scope="row"
                          padding="none"
                        >
                          {dato.expedienteConFecha}
                        </TableCell>
                        <TableCell align="left">
                          {dato.fechaTramiteInterno !== "" && dato.fechaExpediente !== "" && dato.fechaTramiteInterno !== dato.fechaExpediente ? dato.tramiteInternoConFecha : null}
                        </TableCell>
                        <TableCell align="left">
                          {dato.descOrgOrigen}
                        </TableCell>
                        <TableCell align="left">{dato.asunto}</TableCell>
                        <TableCell align="right"><InputLabel id="datoFolios" sx={{ mr: 3 }}>{dato.folios}</InputLabel></TableCell>
                        <TableCell align="left">{dato.soporte}</TableCell>
                        <TableCell align="left">{dato.recibido}</TableCell>
                        <TableCell align="center">
                          <Accion funcion={props.funcion} accion="gestionar">
                            <IconButton
                              title="Gestionar"
                              size="small"
                              component={Link}
                              to={{
                                pathname: "/ver_tramite/" + dato.id,
                                state: { from: location },
                              }}
                            >
                              <AppRegistrationOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Accion>
                          <Accion funcion={props.funcion} accion="editar_tramite">
                            <IconButton
                              title="Editar"
                              size="small"
                              component={Link}
                              disabled={dato.soporte === "DIGITAL"}
                              to={{
                                pathname: "/editar_tramite/" + dato.id,
                                state: { from: location },
                              }}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Accion>
                          <Accion funcion={props.funcion} accion="imprimir_caratula">
                            <IconButton
                              title="Imprimir carátula"
                              fontize="small"
                              onClick={() => { verCaratula(dato.id); handleCloseDocument(); }}
                              disabled={dato.soporte === "PAPEL" && dato.muestra_caratula === "S" ? false : true}
                            >
                              <PrintOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Accion>
                          <Accion funcion={props.funcion} accion="Volante">
                            <IconButton
                              title="Ver volante"
                              onClick={() => verVolante(dato.id)}
                              size="small"
                            >
                              <PictureAsPdfOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Accion>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {submitting ? (
                  <TableRow>
                    <TableCell colSpan="9" align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                    null
                  )}
                {datos.length === 0 && !submitting ? (
                  <TableRow>
                    <TableCell colSpan="9" align="center">
                      <Typography variant="aclaracion">No hay datos para mostrar</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                    null
                  )}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: 53 * emptyRows,
                    }}
                  >
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[
                      5,
                      10,
                      25,
                      { label: "Todos", value: datos.length },
                    ]}
                    count={datos.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    selected={selected}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <Typography variant="aclaracion" gutterBottom>
            {selected.length === 1
              ? " (" + selected.length + " seleccionado)"
              : " (" + selected.length + " seleccionados)"}
          </Typography>
        </CardContent>
      </Card >

      <Modal open={openDocumento} handleClose={handleCloseDocument} closeButton>
        <VisorPdf url={"data:application/octet-stream;base64," + documento} />
      </Modal>
    </MinimalPage >
  );
};

export default BandejaEntrada;